/**
 * Triggers a browser CSV download from an in-memory Blob. Defers the
 * `revokeObjectURL` to the next macrotask — some Chromium/Safari versions
 * abort the download if the blob URL is revoked synchronously after `click()`.
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  try {
    a.click();
  } finally {
    setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
  }
}

/**
 * Pulls a user-facing error message out of an error response body that may be
 * a Blob (the success path uses `responseType: 'blob'`, so error bodies come
 * back wrapped too). Global axios resolves non-2xx (`validateStatus: () => true`),
 * so callers pass the resolved response's status and data rather than a thrown
 * axios error.
 *
 * api-svc error bodies on the download path come in three shapes:
 *   - plain-text body (ValidationException / AuthenticationException)
 *   - JSON `{ errors: [{ message }] }` (InputValidationError)
 *   - JSON `{ message }`
 *   - empty body (404/500) → status-specific fallback
 */
export async function extractBlobErrorMessage(status: number | undefined, data: unknown): Promise<string> {
  const fallback = 'Failed to export. Try again later';

  let text = '';
  if (data instanceof Blob) {
    text = (await blobToText(data)).trim();
  } else if (typeof data === 'string') {
    text = data.trim();
  }

  if (text.length > 0) {
    try {
      const fromJson = messageFromJsonEnvelope(JSON.parse(text));
      if (fromJson) return fromJson;
    } catch {
      // Not JSON — plain-text error message. Surface it directly unless it
      // looks like a gateway HTML error page or an oversized dump.
      if (isDisplayablePlainText(text)) return text;
    }
  }

  if (status === 404) return 'Report not found or not ready to download yet';
  return fallback;
}

/** Blob.prototype.text is missing in some older engines (and jsdom); fall back to FileReader. */
function blobToText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') {
    return blob.text();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

function isDisplayablePlainText(text: string): boolean {
  if (text.length > 300) return false;
  if (text.startsWith('<')) return false;
  return true;
}

function messageFromJsonEnvelope(parsed: unknown): string | undefined {
  if (!parsed || typeof parsed !== 'object') return undefined;
  const obj = parsed as Record<string, unknown>;
  if (typeof obj.message === 'string' && obj.message.length > 0) return obj.message;
  if (Array.isArray(obj.errors) && obj.errors.length > 0) {
    const messages = obj.errors
      .map((e) => (e && typeof e === 'object' ? (e as Record<string, unknown>).message : undefined))
      .filter((m): m is string => typeof m === 'string' && m.length > 0);
    if (messages.length > 0) return messages.join('; ');
  }
  return undefined;
}
